import { Box } from '@mui/material';
import {
  IconButton,
  Typography,
  __SWEEP_DESIGN_TYPOGRAPHY_STYLES___,
  colors,
} from '@sweep-io/sweep-design';
import { RfNodeStandalone } from '../canvas-types/nodeTypesData';
import { NodeProps } from '@xyflow/react';
import { SweepNodeHandles } from './SweepNodeHandles';
import { NODE_HEIGHT, NODE_WIDTH } from '../const';
import { ActionButtonConnectStep } from './components/CanvasButtons';
import { useNodeDimensionsObserver } from './useNodeDimensionsObserver';
import { memo, useCallback } from 'react';
import { MoreActionsHorizontal } from '@sweep-io/sweep-design/dist/icons';
import { ZOOM_LEVEL_CLASSES } from './zoomLevelsConsts';
import { TopTag } from './components/TopTag';
import { NodeEntityTypes } from '../canvas-types';
import { BasedNodeComponent, InnerNodeComponent } from './components/SweepNodeComponent';
import classNames from 'classnames';

export const STANDALONE_NODE_MIN_WIDTH = 246;
const STANDALONE_NODE_ACTION_BUTTON_OFFSET = '-41px';

export const standaloneNodeStyle = {
  '.sweep-standalone-node .standalone-node-paper-header .standalone-node-paper-header-label-1': {
    display: 'none',
  },
  '.sweep-standalone-node .standalone-node-paper-header .standalone-node-paper-header-name': {
    ...__SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4,
  },
  [`&.${ZOOM_LEVEL_CLASSES.LOWER_THAN_0_3}`]: {
    '.sweep-standalone-node .more-button-span': {
      display: 'none !important',
    },
    '.sweep-standalone-node .standalone-node-paper-header .standalone-node-paper-header-label-1': {
      display: 'none !important',
    },
    '.sweep-standalone-node .standalone-node-paper-header .standalone-node-paper-header-name': {
      ...__SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h2,
    },
  },
  [`&.${ZOOM_LEVEL_CLASSES.LOWER_THAN_0_75}`]: {
    '.sweep-standalone-node .standalone-node-paper-header': {
      flexDirection: 'row !important',
      gap: 1,
      alignItems: 'center',
    },
    '.sweep-standalone-node .standalone-node-paper-header .standalone-node-paper-header-label-1': {
      display: 'inline',
    },
    '.sweep-standalone-node .standalone-node-paper-inner': {
      '& > span': {
        display: 'none',
      },
    },
  },
};

const StandaloneNode = ({
  id,
  data,
  dragging,
  width,
  height,
  selected,
}: NodeProps<RfNodeStandalone>) => {
  const {
    label,
    name,
    onConnectClick,
    isHighlighted,
    icon,
    topTag = '',
    onNodeClick,
    color,
  } = data;

  const wrapperRef = useNodeDimensionsObserver({
    id,
    width,
    height,
    minWidth: 0,
  });

  const maybeRenderTopTag = topTag ? <TopTag topTag={topTag} /> : null;

  const _onLabelClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onNodeClick?.({ entity: { type: NodeEntityTypes.Standalone }, id, event });
    },
    [onNodeClick, id],
  );

  const _onMoreClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onNodeClick?.({ entity: { type: NodeEntityTypes.StandaloneMore }, id, event });
    },
    [onNodeClick, id],
  );

  return (
    <Box
      data-testid="sweep-standalone-node"
      className="sweep-standalone-node"
      sx={{
        minWidth: NODE_WIDTH,
        width: 'fit-content',
        cursor: dragging ? 'move' : 'pointer',
        backgroundColor: colors.grey[100],
      }}
      onClick={_onLabelClick}
      ref={wrapperRef}
    >
      {maybeRenderTopTag}

      <BasedNodeComponent
        sx={{
          borderRadius: '4px',
          backgroundColor: color || '#fff',
          minWidth: NODE_WIDTH,
          width: 'fit-content',
          display: 'flex',
          height: NODE_HEIGHT,
        }}
        className={classNames({
          'standalone-node-paper': true,
          shadow: true,
          highlighted: isHighlighted || selected,
        })}
      >
        <SweepNodeHandles>
          <InnerNodeComponent
            sx={{
              padding: '0 12px',
              gap: '2px',
            }}
            className="standalone-node-paper-inner"
          >
            <Box
              sx={{
                position: 'absolute',
                right: STANDALONE_NODE_ACTION_BUTTON_OFFSET,
                top: 0,
                cursor: 'default',
              }}
            >
              {isHighlighted && (
                <ActionButtonConnectStep
                  onClick={(event) => onConnectClick?.(event, { groupId: id }, wrapperRef.current)}
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: 'fit-content',
                minWidth: '100%',
                justifyContent: 'space-between',
              }}
              className="standalone-node-paper-header"
            >
              <Box
                component="span"
                className="standalone-node-paper-header-name"
                whiteSpace="nowrap"
                color={colors.grey[800]}
                display="flex"
                alignItems="center"
                gap={1}
              >
                {icon}
                {name}
              </Box>
              <span className="standalone-node-paper-header-label-1">
                <Typography variant="caption" whiteSpace="nowrap" color={colors.grey[700]}>
                  {label}
                </Typography>
              </span>
              <span className="more-button-span" style={{ marginLeft: 8 }}>
                <IconButton size="tiny" variant="flat" onClick={_onMoreClick}>
                  <MoreActionsHorizontal />
                </IconButton>
              </span>
            </Box>

            <Typography variant="caption" whiteSpace="nowrap" color={colors.grey[700]}>
              {label}
            </Typography>
          </InnerNodeComponent>
        </SweepNodeHandles>
      </BasedNodeComponent>
    </Box>
  );
};

const _memoStandaloneNode = memo(StandaloneNode);

export default _memoStandaloneNode;
